/**
 * Bundled LVGL XML project templates (under runtime resources/templates/).
 */

import { joinFsPath } from '../utils/path';

export type ProjectTemplateId = 'new-project' | 'tutorials' | 'examples';

export type ProjectTemplateKind = 'starter' | 'learn';

export type ProjectTemplateDefinition = {
    id: ProjectTemplateId;
    kind: ProjectTemplateKind;
    label: string;
    icon?: string;
    description: string;
    screens?: number;
    /** Suggested new-folder name in the create-project save dialog */
    defaultFolderName: string;
    /** Path segment relative to resources root, e.g. templates/tutorials */
    pathSegment: string;
};

const PATH_SEP: string = typeof process !== 'undefined' && process?.platform === 'win32' ? '\\' : '/';

export const PROJECT_TEMPLATES: ProjectTemplateDefinition[] = [
    {
        id: 'new-project',
        kind: 'starter',
        label: 'Blank project',
        description: 'Empty LVGL XML project with standard folders and build files.',
        defaultFolderName: 'new_project',
        pathSegment: `templates${PATH_SEP}empty`
    },
    {
        id: 'tutorials',
        kind: 'learn',
        icon: 'bulb',
        label: 'Tutorials',
        description: 'A step-by-step tutorial project to learn LVGL Pro from Hello World to advanced animations.',
        screens: 16,
        defaultFolderName: 'tutorials',
        pathSegment: `templates${PATH_SEP}tutorials`
    },
    {
        id: 'examples',
        kind: 'learn',
        icon: 'grid',
        label: 'Examples',
        description: 'Ready-made example screens demonstrating LVGL XML features.',
        screens: 135,
        defaultFolderName: 'examples',
        pathSegment: `templates${PATH_SEP}examples`
    }
];

export const LEARN_PROJECT_TEMPLATES: ProjectTemplateDefinition[] = PROJECT_TEMPLATES.filter(
    (template) => template.kind === 'learn'
);

export const getProjectTemplate = (templateId?: ProjectTemplateId): ProjectTemplateDefinition => {
    const match: ProjectTemplateDefinition | undefined = PROJECT_TEMPLATES.find(
        (template) => template.id === templateId
    );
    return match ?? PROJECT_TEMPLATES[0];
};

export const getProjectTemplatePathSegment = (templateId?: ProjectTemplateId): string =>
    getProjectTemplate(templateId).pathSegment;

export const getProjectTemplateDefaultFolderName = (templateId?: ProjectTemplateId): string =>
    getProjectTemplate(templateId).defaultFolderName;

export const BASIC_PROJECT_TEMPLATE_PATH_SEGMENT = joinFsPath('templates', 'basic');
